<template>
  <Modal
    :show="showModal"
    size="md"
    dialog-max-width="550px"
    @close="closeModal"
    headerVariant="blue"
    :blur-backdrop="false"
    :backdropOpacity="0.3"
    :zIndex="10070"
    :show-close-button="false"
  >
    <template #header>
      <div class="edit-note-header">
        <div class="edit-note-header__text">
          <h2 class="edit-note-header__title">Edit Note</h2>
          <p class="edit-note-header__subtitle">Update note content</p>
        </div>
        <button
          type="button"
          class="edit-note-header__close"
          @click="closeModal"
          aria-label="Close modal"
        >
          <Icon icon="mdi:close" />
        </button>
      </div>
    </template>
    <template #default>
      <div class="modal-body-content">
        <div class="form-group">
          <label class="form-label">Note</label>
          <RichTextEditor
            ref="editEditor"
            v-model="editNoteObj.message"
            height="180px"
            :toolbar="notesToolbar"
            :accepts-uploads="true"
            @upload="handleEditorUpload"
            @uploads-pending="pendingUploads = $event"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <Button variant="secondary" @click="closeModal"> Cancel </Button>
      <Button
        variant="primary"
        :loading="isLoading"
        :disabled="!editNoteObj.message?.trim() || pendingUploads > 0"
        @click="editNote"
      >
        Save
      </Button>
    </template>
  </Modal>
  <ActionConfirmationModal
    :show="showCloseConfirm"
    title="Discard upload?"
    message="Uploads are still in progress. Closing now will cancel them."
    confirm-text="Discard"
    cancel-text="Keep open"
    @close="showCloseConfirm = false"
    @confirm="confirmCloseAndDiscard"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { Modal, Button, RichTextEditor, Icon } from "@/components/ui";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import { useToast } from "@/composables/ui/useToast";
import { useFormDraft } from "@/composables/shared";
import { useEditNote, useUploadNoteAttachment } from "@/composables/notes";

// Props
interface Props {
  selectedNote: {
    id: string;
    message: string;
    contactId: string;
    [key: string]: any;
  };
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  closeEditNoteModal: [];
  refreshNotes: [];
}>();

// Store (only for account)
const store = useStore();
const account = computed(() => store.getters.account.data);

// Edit note mutation
const editNoteMutation = useEditNote();
const isLoading = computed(() => editNoteMutation.isPending.value);

const uploadToNote = useUploadNoteAttachment();
const pendingUploads = ref(0);
const showCloseConfirm = ref(false);
const toast = useToast();
let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

const handleEditorUpload = async (payload: {
  file: File;
  kind: "image" | "video" | "file";
  placeholderId?: string;
  signal?: AbortSignal;
  onProgress?: (pct: number) => void;
  onSuccess?: (url: string) => void;
  onError?: (msg?: string) => void;
}) => {
  if (!props.selectedNote?.id) {
    payload.onError?.("Missing note id");
    return;
  }
  try {
    const result = await uploadToNote.mutateAsync({
      noteId: props.selectedNote.id,
      files: [payload.file],
      onProgress: payload.onProgress,
      signal: payload.signal,
    });
    const uploaded = result?.[0];
    if (uploaded?.url && payload.onSuccess) {
      payload.onSuccess(uploaded.url);
    } else {
      payload.onError?.("Upload failed");
    }
  } catch (err: any) {
    if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
    payload.onError?.(err?.message || "Upload failed");
  }
};

watch(pendingUploads, (n) => {
  if (n > 0 && !beforeUnloadHandler) {
    beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Uploads in progress. Leave anyway?";
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
  } else if (n === 0 && beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
});

// State
const showModal = ref(true);
const editNoteObj = ref({
  id: null as string | null,
  message: "" as string,
  contactId: null as string | null,
});

const notesToolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image", "attach-file"],
  ["clean"],
];

// Template ref
const editEditor = ref<any>(null);

const { loadDraft: loadEditNoteDraft, clearDraft: clearEditNoteDraft } = useFormDraft(editNoteObj, {
  key: `edit-note:${props.selectedNote?.id}`,
  fields: ["message"],
  isEmpty: (v) => !v.message?.trim(),
});

// Methods
const setValues = () => {
  if (props.selectedNote) {
    editNoteObj.value.id = props.selectedNote.id;
    editNoteObj.value.message = props.selectedNote.message || "";
    editNoteObj.value.contactId = props.selectedNote.contactId;
  }
  const draft = loadEditNoteDraft();
  if (draft?.message?.trim()) {
    editNoteObj.value.message = draft.message;
  }
};

const closeModal = () => {
  if (editEditor.value?.hasPendingUploads?.()) {
    showCloseConfirm.value = true;
    return;
  }
  emit("closeEditNoteModal");
};

const confirmCloseAndDiscard = () => {
  showCloseConfirm.value = false;
  editEditor.value?.cancelAllUploads?.();
  emit("closeEditNoteModal");
};

const editNote = async () => {
  if (!editNoteObj.value.message?.trim()) return;
  if (pendingUploads.value > 0) {
    toast.warning("Please wait for uploads to finish.");
    return;
  }
  const id = editNoteObj.value.id ?? "";
  const contactId = editNoteObj.value.contactId ?? "";
  if (!id || !contactId) return;

  if (editEditor.value?.stripAllPlaceholders) {
    editEditor.value.stripAllPlaceholders();
    const fresh = editEditor.value?.getHTML?.();
    if (typeof fresh === "string") editNoteObj.value.message = fresh;
  }

  try {
    const result = await editNoteMutation.mutateAsync({
      id,
      message: editNoteObj.value.message,
      contactId,
      organizationId: account.value?.organizationId ?? "",
    });

    if (result) {
      clearEditNoteDraft();
      emit("refreshNotes");
      emit("closeEditNoteModal");
    }
  } catch (error) {
    console.error("Error editing note:", error);
  }
};

onMounted(() => {
  setValues();
});

onBeforeUnmount(() => {
  editEditor.value?.cancelAllUploads?.();
  if (beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
});
</script>

<style scoped>
.edit-note-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.edit-note-header__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.edit-note-header__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}

.edit-note-header__subtitle {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.3;
}

.edit-note-header__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}

.edit-note-header__close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.edit-note-header__close .iconify {
  width: 16px;
  height: 16px;
}

.modal-body-content {
  padding: 12px 24px;
}

.form-group {
  margin-bottom: 0;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  display: block;
}

:deep(.ql-toolbar) {
  padding: 6px 10px;
  font-size: 11px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-color: #e5e7eb;
  background-color: #f9fafb;
}

:deep(.ql-toolbar .ql-formats) {
  margin-right: 8px;
}

:deep(.ql-toolbar button) {
  padding: 3px 5px;
  width: 24px;
  height: 24px;
}

:deep(.ql-toolbar .ql-picker) {
  font-size: 12px;
}

:deep(.ql-container) {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border-color: #e5e7eb;
  font-size: 0.875rem;
}

:deep(.ql-editor) {
  min-height: 140px;
}

:deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: normal;
}
</style>
